// Shared redaction of personal data and credentials before anything leaves the app
// through a logging or error-reporting sink (console, Sentry).
//
// Two layers:
//  1. Key-based: any property whose name is in SENSITIVE_KEYS is replaced entirely.
//  2. Pattern-based: any string is run through SCRUB_PATTERNS (e-mail, phone, coordinates,
//     JWT, token-like assignments).
//
// This is a safety net. Call sites should still log derived facts (ids, counts, status)
// rather than raw values. Kept as CommonJS on purpose: it is required from server/index.js
// (plain Node) via ./log.js as well as bundled by webpack. For that reason it also avoids
// syntax that needs Babel helpers (spread, for-of, destructuring): those are injected as ESM
// imports, which makes webpack treat the file as an ES module and drop module.exports.

const SENSITIVE_KEYS = [
	'token', 'rt', 'refreshtoken', 'accesstoken', 'idtoken', 'authorization', 'password',
	'secret', 'apikey', 'arckey',
	'email', 'notifyemail', 'phone', 'mobile', 'phonenumber', 'username',
	'firstname', 'lastname', 'fullname', 'address', 'street', 'zipcode', 'postalcode', 'city',
	'lat', 'lng', 'lon', 'latitude', 'longitude', 'position', 'location', 'coords', 'coordinates',
	'ip', 'ip_address', 'ipaddress', 'imei', 'iccid', 'msisdn'
];

const SCRUB_PATTERNS = [
	// e-mail. The local part is bounded: an unbounded [\w.+-]+ backtracks quadratically on long
	// runs of word characters without an "@" (minified stacks, base64) and can freeze the tab.
	[/[\w.+-]{1,64}@[\w-]+\.[\w.]{2,}/g, '<email>'],
	// NL mobile numbers, and any international-format number
	[/(?:\+31|0031|\b0)[\s-]?6[\s-]?\d{8}\b/g, '<phone>'],
	[/\+\d{1,3}[\s-]?\d{6,13}\b/g, '<phone>'],
	// a "lat,lng" pair with 3+ decimals, so ordinary numbers are left alone
	[/-?\d{1,3}\.\d{3,}\s*,\s*-?\d{1,3}\.\d{3,}/g, '<coord>'],
	// the same as a named field, which the pair pattern cannot catch
	[/("?(?:latitude|longitude|lat|lng|lon)"?\s*[:=]\s*)-?\d+\.\d+/gi, '$1<coord>'],
	// JWT by shape
	[/\beyJ[A-Za-z0-9_-]{10,}\.[A-Za-z0-9_-]+\.[A-Za-z0-9_-]*/g, '<jwt>'],
	// credentials by key name inside serialized text. "Bearer" is a scheme, not the secret, and a
	// value that already starts with "<" is a placeholder left by an earlier pattern (e.g. <jwt>).
	[/("?(?:token|refreshToken|accessToken|\brt|authorization|password|secret|apiKey)"?\s*[:=]\s*"?(?:Bearer\s+)?)(?!<|Bearer\b)[^",}\s]+/gi, '$1<redacted>']
];

const REDACTED = '<redacted>';
const MAX_DEPTH = 8;
const MAX_STRING_LENGTH = 2000;
const MAX_STACK_LENGTH = 20000;

const isSensitiveKey = key => SENSITIVE_KEYS.indexOf(String(key).toLowerCase()) !== -1;

const applyPatterns = value => {
	let out = value;
	for (let i = 0; i < SCRUB_PATTERNS.length; i++) {
		out = out.replace(SCRUB_PATTERNS[i][0], SCRUB_PATTERNS[i][1]);
	}
	return out;
};

const truncate = (value, max) => (value.length > max ? `${value.slice(0, max)}…` : value);

const scrubString = value => {
	if (typeof value !== 'string') return value;
	// truncate first: the pattern cost is then bounded by MAX_STRING_LENGTH
	return applyPatterns(truncate(value, MAX_STRING_LENGTH));
};

const scrubValue = (value, depth = 0, seen = new WeakSet()) => {
	if (value == null) return value;
	if (typeof value === 'string') return scrubString(value);
	if (typeof value !== 'object') return value;
	if (depth >= MAX_DEPTH) return '[depth limit]';
	if (seen.has(value)) return '[circular]';

	if (value instanceof Error) {
		const copy = new Error(scrubString(value.message));
		copy.name = value.name;
		// stacks are diagnostics: keep far more than an ordinary string, but still bounded
		copy.stack = typeof value.stack === 'string' ? applyPatterns(truncate(value.stack, MAX_STACK_LENGTH)) : value.stack;
		seen.add(value);
		Object.keys(value).forEach(key => {
			if (key === 'message' || key === 'stack' || key === 'name') return;
			copy[key] = isSensitiveKey(key) ? REDACTED : scrubValue(value[key], depth + 1, seen);
		});
		return copy;
	}
	if (value instanceof Date) return value;
	// Immutable.js records/maps/lists
	if (typeof value.toJS === 'function') {
		return scrubValue(value.toJS(), depth, seen);
	}

	seen.add(value);
	if (Array.isArray(value)) {
		return value.map(v => scrubValue(v, depth + 1, seen));
	}
	const out = {};
	Object.keys(value).forEach(key => {
		out[key] = isSensitiveKey(key) ? REDACTED : scrubValue(value[key], depth + 1, seen);
	});
	return out;
};

// Replaces numeric ids in a URL path with a placeholder so transaction names and
// breadcrumb URLs cannot single out a device, alarm or user.
const scrubUrl = url => {
	if (typeof url !== 'string') return url;
	const parts = url.split('?');
	const path = parts[0];
	const query = parts[1];
	const normalizedPath = path.replace(/\/\d+(?=\/|$)/g, '/:id');
	return scrubString(query ? `${normalizedPath}?<query>` : normalizedPath);
};

module.exports = {
	SENSITIVE_KEYS,
	SCRUB_PATTERNS,
	REDACTED,
	isSensitiveKey,
	scrubString,
	scrubValue,
	scrubUrl
};
